// cart-items.js

// Guard so we don't crash if this file is accidentally loaded twice
if (!customElements.get('cart-items')) {
  class CartItems extends HTMLElement {
    constructor() {
      super();

      this.itemsRoot = null;        // <cart-drawer-items id="CartDrawer-Items">
      this.itemsContainer = null;   // <div id="CartDrawer-CartItems">
      this.lineItemTemplate = null; // <template id="CartDrawer-LineItemTemplate">

      this.loading = false;
    }

    connectedCallback() {
      this.itemsRoot = this.querySelector('#CartDrawer-Items');
      this.itemsContainer = this.querySelector('#CartDrawer-CartItems');
      this.lineItemTemplate = this.querySelector('#CartDrawer-LineItemTemplate');

      if (!this.itemsRoot || !this.itemsContainer || !this.lineItemTemplate) {
        console.warn(
          '[cart-items] Missing required elements inside <cart-items>. ' +
          'Expected #CartDrawer-Items, #CartDrawer-CartItems and #CartDrawer-LineItemTemplate.'
        );
        return;
      }

      // Initial fetch of cart
      this.fetchCart();

      // Listen for external cart updates (e.g. add-to-cart)
      document.addEventListener('cart:refresh', (event) => {
        if (event.detail && event.detail.cart) {
          this.render(event.detail.cart);
        } else {
          this.fetchCart();
        }
      });
    }

    // ---- Public API called by CartDrawer ----
    render(cart) {
      if (!cart) return;

      // Toggle empty states
      const isEmpty = !cart.items || cart.items.length === 0;

      this.classList.toggle('is-empty', isEmpty);

      const drawer = this.closest('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('is-empty', isEmpty);
      }

      const emptyState = drawer?.querySelector('.drawer__inner-empty');
      if (emptyState) {
        emptyState.style.display = isEmpty ? '' : 'none';
      }

      // Clear existing content
      this.itemsContainer.innerHTML = '';

      if (isEmpty) {
        this.updateTotals(cart);
        return;
      }

      // Create list container
      const listEl = document.createElement('ul');
      listEl.className = 'cart-items-list';
      this.itemsContainer.appendChild(listEl);

      // Populate items from template
      cart.items.forEach((item) => {
        const li = this.buildLineItem(item);
        if (li) listEl.appendChild(li);
      });

      // Update subtotal / checkout button
      this.updateTotals(cart);

      // Bind events for quantity and remove
      this.attachItemEvents();
    }

    // ---- Internal helpers ----

    fetchCart() {
      this.setLoading(true);

      fetch('/cart.js', {
        method: 'GET',
        headers: { Accept: 'application/json' },
      })
        .then((res) => res.json())
        .then((cart) => {
          this.render(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .catch((err) => {
          console.error('[cart-items] Failed to fetch cart.js', err);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    buildLineItem(item) {
      if (!this.lineItemTemplate) return null;

      const fragment = this.lineItemTemplate.content.cloneNode(true);
      const li = fragment.querySelector('.cart-item') || fragment.firstElementChild;

      if (!li) return null;

      li.dataset.key = item.key || '';

      // Image
      const imgEl = li.querySelector('.cart-item__image img');
      if (imgEl && item.image) {
        imgEl.src = item.image;
        imgEl.alt = item.product_title || item.title || '';
      }

      // Name
      const nameEl = li.querySelector('.cart-item__name');
      if (nameEl) {
        nameEl.textContent = item.product_title || item.title || '';
      }

      // Price (line price)
      const priceEl = li.querySelector('.cart-item__price');
      if (priceEl) {
        const linePrice = typeof item.final_line_price === 'number'
          ? item.final_line_price
          : item.line_price;

        priceEl.textContent = this.formatMoney(linePrice);
      }

      // Quantity
      const qtyInput = li.querySelector('.quantity-input');
      if (qtyInput) {
        qtyInput.value = item.quantity;
        qtyInput.setAttribute('min', '1');
        qtyInput.dataset.key = item.key;
      }

      return li;
    }

    attachItemEvents() {
      // Quantity +/- buttons
      this.querySelectorAll('.quantity-button').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          const button = e.currentTarget;
          const li = button.closest('.cart-item');
          if (!li) return;

          const key = li.dataset.key;
          const qtyInput = li.querySelector('.quantity-input');
          if (!key || !qtyInput) return;

          const action = button.dataset.action;
          let currentQty = parseInt(qtyInput.value, 10) || 1;

          if (action === 'increase') {
            currentQty += 1;
          } else if (action === 'decrease') {
            currentQty = Math.max(currentQty - 1, 0);
          }

          this.updateLine(key, currentQty);
        });
      });

      // Remove buttons
      this.querySelectorAll('.cart-item__remove').forEach((btn) => {
        btn.addEventListener('click', (e) => {
          e.preventDefault();

          const li = btn.closest('.cart-item');
          if (!li) return;

          const key = li.dataset.key;
          if (!key) return;

          this.updateLine(key, 0);
        });
      });
    }

    updateLine(key, quantity) {
      this.setLoading(true);

      fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: key,
          quantity: quantity,
        }),
      })
        .then((res) => res.json())
        .then((cart) => {
          this.render(cart);
          document.dispatchEvent(new CustomEvent('cart:updated', { detail: { cart } }));
        })
        .catch((err) => {
          console.error('[cart-items] Failed to update line item', err);
        })
        .finally(() => {
          this.setLoading(false);
        });
    }

    updateTotals(cart) {
      const drawer = this.closest('cart-drawer');
      if (!drawer || !cart) return;

      const checkoutBtn = drawer.querySelector('#CartDrawer-ViewCart');
      const amountSpan = checkoutBtn?.querySelector('span');

      const totalPrice = cart.total_price || 0;
      const formatted = this.formatMoney(totalPrice);

      if (amountSpan) {
        amountSpan.textContent = ` ${formatted} USD`;
      }

      if (checkoutBtn) {
        checkoutBtn.disabled = cart.items.length === 0;
      }

      // You can also update any subtotal element here if you add one in the markup
      // e.g. const subtotalEl = drawer.querySelector('.cart-subtotal');
      // if (subtotalEl) subtotalEl.textContent = formatted + ' USD';
    }

    setLoading(isLoading) {
      this.loading = isLoading;

      const drawer = this.closest('cart-drawer');
      if (drawer) {
        drawer.classList.toggle('cart-drawer--loading', isLoading);
      }
    }

    formatMoney(cents) {
      const value = (cents || 0) / 100;
      return `$${value.toFixed(2)}`;
    }
  }

  customElements.define('cart-items', CartItems);
}
